import { Dispatch, UnknownAction } from "@reduxjs/toolkit";
import BaseApp, { IBaseAppDataDisplayerConfig, IBaseAppDynamicBasePage, IBaseAppFormConfig, IGridDataAdditional } from "../BaseApp";
import CreateOrUpdateLocation from "./CreateOrUpdateLocation";
import { addNewLocation, deleteLocation, setSelectedLocations, updateAllLocations, updateLocation, updateLocationsLoadingStatus, updateLocationsPagination, updateLocationsSearch, updateLocationsSearchParams, useReturnAllLocations, useReturnLocationsLoadingStatus, useReturnLocationsPagination, useReturnLocationsSearch, useReturnLocationsSearchParams, useReturnSelectedLocations } from "./locationsSlice";
import { GridDataColumns, IGridDataConfig } from "../../components/GridData";
import { ICellRendererParams } from "ag-grid-community";
import { Link } from "react-router-dom";
import { JSX } from "react/jsx-runtime";

export interface ILocationModel {
    id: number,
    title: string,
    country: string,
    address: string,
    latitude: number,
    longitude: number,
    thumbnail: string,
    created_by: number,
    date_created: string,
    last_updated: string,
    is_active: boolean,
    maps_link: string,
}

export const emptyLocatiomModel: ILocationModel = {
    id: 0,
    title: "",
    country: "",
    address: "",
    latitude: 0,
    longitude: 0,
    thumbnail: "",
    created_by: 0,
    date_created: "",
    last_updated: "",
    is_active: false,
    maps_link: ""
}

/**
 * Locations App
 */
export default class LocationsApp extends BaseApp {

    appName = "locations"
    appBaseEndPoint = "/warehouses/locations"
    customListEndPoint = this.appBaseEndPoint

    elemPrefix: string = "locations_"

    messages = {
        create: "Create a new location",
        createShort: "New location",
        edit: "Edit location",
        editShort: "Edit",
        delete: "Delete location",
        deleteShort: "Delete",
        view: "View location",
        nothingFound: "No locations have been found.",
    }

    /**
     * Pages
     */
    pages = {
        createOrUpdate: CreateOrUpdateLocation
    }

    dataDisplayer: IBaseAppDataDisplayerConfig = {
        columns: [
            {
                id: "thumbnail",
                type: "thumbnail",
                label: "Thumbnail"
            },
            {
                id: "title",
                type: "text",
                label: "Title",
            },
            {
                id: "country",
                type: "country",
                label: "Country",
            },
            {
                id: "address",
                type: "text",
                label: "Address",
                maxWidth: 300,
                minWidth: 300
            },
            {
                id: "maps_link",
                type: "map",
                label: "Google Maps",
            },
        ],
        notionFound: {
            title: "Nothing...",
            message: "No locations have been found :("
        }
    }

    formConfig: IBaseAppFormConfig = {
        form: {
            name: "locations-form",
            elements: [
                {
                    id: this.elemPrefix + "title",
                    xs: 8,
                    type: "TextField",
                    label: "Title",
                    fullWidth: true
                },
                {
                    id: this.elemPrefix + "country",
                    xs: 4,
                    type: "SelectCountry",
                    label: "Country",
                    fullWidth: true,
                },
                {
                    id: this.elemPrefix + "address",
                    xs: 12,
                    type: "TextField",
                    label: "Address",
                    fullWidth: true,
                    multiline: true,
                    minRows: 4,
                    maxRows: 4,
                },
                {
                    id: this.elemPrefix + "maps_link",
                    xs: 12,
                    type: "GoogleMap",
                    label: "Google Embed Maps",
                    fullWidth: true,
                },
                // {
                //     id: this.elemPrefix + "latitude",
                //     xs: 6,
                //     type: "TextField",
                //     fieldType: "number",
                //     label: "Latitude",
                //     fullWidth: true
                // },
                // {
                //     id: this.elemPrefix + "longitude",
                //     xs: 6,
                //     type: "TextField",
                //     fieldType: "number",
                //     label: "Longitude",
                //     fullWidth: true
                // },
                {
                    id: this.elemPrefix + "thumbnail",
                    xs: 12,
                    type: "ImageUploader",
                    label: "Thumbnail",
                    fullWidth: true
                },
            ]
        },
        sectionBox: {
            showTitle: true,
            title: "Details",
            description: "Fill in the item details"
        },
        requiredFields: [
            {
                id: this.elemPrefix + "title",
                message: "Title is required!"
            },
            {
                id: this.elemPrefix + "country",
                message: "Country is required!"
            },
            {
                id: this.elemPrefix + "address",
                message: "Address is required!"
            },
            {
                id: this.elemPrefix + "latitude",
                message: "Latitude is required!"
            },
            {
                id: this.elemPrefix + "longitude",
                message: "Longitude is required!"
            }
        ]
    }

   /**
     * Dynamic base page
     */
   dynamicBasePage: IBaseAppDynamicBasePage = {
        items: useReturnAllLocations,
        pagination: useReturnLocationsPagination,
        loading: useReturnLocationsLoadingStatus,
        search: useReturnLocationsSearch,
        updateLoadingStatus: (status: boolean, dispatch: Dispatch<UnknownAction>) => dispatch(updateLocationsLoadingStatus({ loading: status })),
        deleteItem: (id: number, dispatch: Dispatch<UnknownAction>) => dispatch(deleteLocation({ id: id })),
        addNewItem: (item, dispatch) => dispatch(addNewLocation((item as ILocationModel))),
        updateItem: (item, dispatch) => dispatch(updateLocation((item as ILocationModel))),
        updateAllItems: (items, dispatch) => dispatch(updateAllLocations((items as ILocationModel[]))),
        updateSearch: (search: string, dispatch: Dispatch<UnknownAction>) => dispatch(updateLocationsSearch({ search: search })),
        updateItemsPagination: (pagination, dispatch) => dispatch(updateLocationsPagination(pagination)),
        itemsActionOptions: [
            {
                icon: "edit",
                label: "Edit",
                link: "/panel/locations/edit/[id]",
                onClick: (rawItem) => {
                    // navigate("/panel/items/edit/" + rawItem.id)
                },
            },
            {
                icon: "show",
                label: "View",
                link: "/panel/locations/view/[id]",
                onClick: (rawItem) => {
                    // navigate("/panel/items/view/" + rawItem.id)
                },
            },
        ],
    }


    /**
     * Grid data
     */
    gridDataColumns: GridDataColumns = [
        // {
        //     field: "thumbnail",
        //     headerName: "thumbnail",
        //     type: "thumbnail",
        //     resizable: false,
        //     width: 110,
        // },
        // {
        //     field: "title",
        //     headerName: "Title",
        //     width: 250,
        //     cellRenderer: (params: ICellRendererParams<ILocationModel>) => {
        //         const item = params.data

        //         if (!item){
        //             return params.value
        //         }

        //         return(
        //             <div className="grid-data-current-item-location-box">
        //                 <Link className="data-grid-link" to={`/panel/locations/view/${item.id}`}>
        //                     <h3 className="title">{item.title}</h3>
        //                 </Link>
        //             </div>
        //         )
        //     }
        // },
        {
            field: "title",
            headerName: "Location",
            type: "title",
            width: 350,
            minWidth: 350,
            cellRendererParams: {
                appClass: () => new LocationsApp(),
                enableQuickPreview: true,
            }
        },
        {
            field: "address",
            headerName: "Address",
            width: 300,
        },
        {
            field: "country",
            headerName: "Country",
            type: "country",
        },
        {
            field: "date_created",
            headerName: "Date Created",
            type: "date",
            width: 250,
        }
    ]
    useGridData: boolean = true
    gridConfig: IGridDataConfig = {
        viewItemLink: "/panel/locations/view/[id]",
        editItemLink: "/panel/locations/edit/[id]",
    }
    gridDataEssentials: IGridDataAdditional  = {
        selectedItems: useReturnSelectedLocations,
        searchParams: useReturnLocationsSearchParams,
        actionButtons: [],
        headerOptions: [],
        setSelectedItems: (items, dispatch) => dispatch(setSelectedLocations(items)),
        updateItemSearchParams: (params, dispatch) => dispatch(updateLocationsSearchParams(params))
    }

}